"use client";

import useEmblaCarousel from "embla-carousel-react";
import TestimonialCard from "./ui/TestimonialCard";
import { ReactTyped } from "react-typed";
import Image from "next/image";
import { SanityTalentStory } from "@/sanity/lib/types";
import { urlFor } from "@/sanity/lib/image";
import TrackedLink from "./ui/TrackedLink";

export default function Testimonials({
  items,
  title = ["Real Stories.", "Real Stories. Real Impact."],
}: {
  items: SanityTalentStory[];
  title?: string[];
  linkHref?: string;
  linkText?: string;
}) {
  const hasMultipleStories = items.length > 1;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: hasMultipleStories,
    duration: 80,
  });

  return (
    <section className="selection:!bg-accent my-20 flex w-full flex-col items-stretch justify-center overflow-hidden md:my-30 lg:my-40 lg:snap-center">
      <div className="relative mx-auto w-full overflow-hidden px-6 lg:max-w-300">
        <h2 className="text-accent mb-8 h-[2lh] text-center !text-5xl leading-[1] !font-bold uppercase md:h-lh lg:text-left lg:!text-6xl">
          {" "}
          <ReactTyped
            strings={title}
            typeSpeed={100}
            startWhenVisible
            startDelay={200}
          ></ReactTyped>
        </h2>
        <div className="w-full" ref={emblaRef}>
          <div className="flex touch-pan-y">
            {items?.map((item) => (
              <div
                className="w-full shrink-0 grow-0 md:w-100 lg:w-122"
                key={item._id}
              >
                <TestimonialCard
                  name={item.name}
                  quote={item.quote}
                  photoUrl={urlFor(item.photo).width(180).height(180).url()}
                />
              </div>
            ))}
          </div>
        </div>
        {hasMultipleStories ? (
          <div className="absolute right-6 bottom-100 flex scale-100 flex-col items-center gap-2 md:right-0 lg:top-0 lg:right-0 -translate-x-1/2">
            <button
              type="button"
              onClick={() => emblaApi?.scrollNext()}
              className="bg-white/85 order-2 aspect-square w-10 cursor-pointer rounded-full backdrop-blur-lg md:w-12 lg:order-2"
            >
              <Image
                width={48}
                height={48}
                alt="next icon"
                src={"/icons/arrow.svg"}
              />
            </button>
            <button
              type="button"
              onClick={() => emblaApi?.scrollPrev()}
              className="bg-white/85 aspect-square w-10 cursor-pointer rounded-full backdrop-blur-lg md:w-12"
            >
              <Image
                width={48}
                height={48}
                alt="previous icon"
                src={"/icons/arrow.svg"}
                className="rotate-180"
              />
            </button>
          </div>
        ) : null}
        <div className="mt-4 text-right lg:mx-auto lg:max-w-300">
          <TrackedLink
            href="/talent-stories"
            className="text-accent px-2 text-xl font-bold hover:underline"
            eventName="talent_stories_read_more_clicked"
          >
            Read more →
          </TrackedLink>
        </div>
      </div>
    </section>
  );
}
